/**
 * AdminMessage Component
 * Displays admin announcements at the top of the settings modal
 *
 * Edit /src/config/adminMessage.json to change the message
 *
 * Types:
 * - info: Blue, informational
 * - warning: Orange/amber, alerts
 * - promo: Purple/gradient, promotions
 * - success: Green, positive news
 * - maintenance: Gray, service status
 */

import React, { useState, useEffect } from 'react'
import {
  Info,
  AlertTriangle,
  Gift,
  CheckCircle,
  Wrench,
  X,
  ExternalLink,
  Sparkles
} from 'lucide-react'
import adminConfig from '@config/adminMessage.json'

// Storage key for dismissed messages
const DISMISSED_KEY = 'emd_dismissed_admin_msg'

/**
 * Get icon component for message type
 */
function getIcon(type) {
  const icons = {
    info: Info,
    warning: AlertTriangle,
    promo: Gift,
    success: CheckCircle,
    maintenance: Wrench,
  }
  return icons[type] || Info
}

/**
 * Get styles for message type
 */
function getStyles(type) {
  const styles = {
    info: {
      container: 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
      icon: 'text-blue-500 bg-blue-100',
      title: 'text-blue-900',
      message: 'text-blue-700',
      link: 'text-blue-600 hover:text-blue-800',
      dismiss: 'text-blue-400 hover:text-blue-600 hover:bg-blue-100',
    },
    warning: {
      container: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200',
      icon: 'text-amber-500 bg-amber-100',
      title: 'text-amber-900',
      message: 'text-amber-700',
      link: 'text-amber-600 hover:text-amber-800',
      dismiss: 'text-amber-400 hover:text-amber-600 hover:bg-amber-100',
    },
    promo: {
      container: 'bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border-purple-200',
      icon: 'text-purple-500 bg-purple-100',
      title: 'text-purple-900',
      message: 'text-purple-700',
      link: 'text-purple-600 hover:text-purple-800 font-semibold',
      dismiss: 'text-purple-400 hover:text-purple-600 hover:bg-purple-100',
      special: true, // Add sparkles animation
    },
    success: {
      container: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200',
      icon: 'text-green-500 bg-green-100',
      title: 'text-green-900',
      message: 'text-green-700',
      link: 'text-green-600 hover:text-green-800',
      dismiss: 'text-green-400 hover:text-green-600 hover:bg-green-100',
    },
    maintenance: {
      container: 'bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200',
      icon: 'text-slate-500 bg-slate-100',
      title: 'text-slate-900',
      message: 'text-slate-600',
      link: 'text-slate-600 hover:text-slate-800',
      dismiss: 'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
    },
  }
  return styles[type] || styles.info
}

/**
 * Check if message should be shown
 */
function shouldShowMessage(config) {
  if (!config.enabled) return false

  // Check expiration
  if (config.expiresAt) {
    const expiry = new Date(config.expiresAt)
    if (new Date() > expiry) return false
  }

  // Check if dismissed (using message hash)
  if (config.dismissible) {
    const msgHash = btoa(`${config.type}:${config.title}:${config.message}`).slice(0, 20)
    const dismissed = localStorage.getItem(DISMISSED_KEY)
    if (dismissed === msgHash) return false
  }

  return true
}

/**
 * Dismiss the current message
 */
function dismissMessage(config) {
  const msgHash = btoa(`${config.type}:${config.title}:${config.message}`).slice(0, 20)
  localStorage.setItem(DISMISSED_KEY, msgHash)
}

export default function AdminMessage({ onNavigate }) {
  const [visible, setVisible] = useState(false)
  const [config, setConfig] = useState(adminConfig)

  useEffect(() => {
    // Check if message should be shown
    setVisible(shouldShowMessage(config))
  }, [config])

  const handleDismiss = () => {
    dismissMessage(config)
    setVisible(false)
  }

  const handleLink = (e) => {
    if (config.link?.startsWith('#')) {
      e.preventDefault()
      // Internal navigation (tab switch)
      const tab = config.link.slice(1)
      onNavigate?.(tab)
    }
    // External links open normally
  }

  if (!visible) return null

  const Icon = getIcon(config.type)
  const styles = getStyles(config.type)

  return (
    <div className={`
      relative rounded-xl border p-4 mb-4
      ${styles.container}
      transition-all duration-300 ease-in-out
    `}>
      {/* Dismiss button */}
      {config.dismissible && (
        <button
          onClick={handleDismiss}
          className={`
            absolute top-2 right-2 p-1.5 rounded-lg
            transition-colors duration-200
            ${styles.dismiss}
          `}
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`
          flex-shrink-0 p-2 rounded-lg
          ${styles.icon}
        `}>
          {styles.special ? (
            <div className="relative">
              <Icon className="w-5 h-5" />
              <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-yellow-400 animate-pulse" />
            </div>
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-6">
          {config.title && (
            <h4 className={`font-semibold text-sm mb-0.5 ${styles.title}`}>
              {config.title}
            </h4>
          )}

          <p className={`text-sm ${styles.message}`}>
            {config.message}
          </p>

          {/* Link */}
          {config.link && config.linkText && (
            <a
              href={config.link}
              onClick={handleLink}
              className={`
                inline-flex items-center gap-1 mt-2 text-sm
                ${styles.link}
                transition-colors duration-200
              `}
            >
              {config.linkText}
              {!config.link.startsWith('#') && (
                <ExternalLink className="w-3.5 h-3.5" />
              )}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Preview component for testing different message types
 * Usage: <AdminMessagePreview type="promo" />
 */
export function AdminMessagePreview({ type = 'info' }) {
  const examples = adminConfig._examples
  const example = examples[type] || examples.info

  const config = {
    enabled: true,
    dismissible: false,
    ...example,
  }

  const Icon = getIcon(config.type)
  const styles = getStyles(config.type)

  return (
    <div className={`
      relative rounded-xl border p-4
      ${styles.container}
    `}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 p-2 rounded-lg ${styles.icon}`}>
          {styles.special ? (
            <div className="relative">
              <Icon className="w-5 h-5" />
              <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-yellow-400 animate-pulse" />
            </div>
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </div>

        <div className="flex-1">
          <h4 className={`font-semibold text-sm mb-0.5 ${styles.title}`}>
            {config.title}
          </h4>
          <p className={`text-sm ${styles.message}`}>
            {config.message}
          </p>
          {config.linkText && (
            <span className={`inline-flex items-center gap-1 mt-2 text-sm ${styles.link}`}>
              {config.linkText}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
